import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import "../css/InfoModal.css";
import Icon from "../../generic/Icon";
import { useDrag, useDrop } from "react-dnd";
import useApi from "../../../apiProvider";
import styles from "../../generic/css/Table.module.css";
import { useSiteContext } from "../Providers/SSCProvider";
import { filterKeys } from "../Constants/filterKeys";
import { useRoleContext } from "../Providers/RoleProvider";
import { FlagIcon } from "react-flag-kit";
import countries from "i18n-iso-countries";

const SOURCE_W = 320;
const DEST_W = 320;

const leftCellStyle = (isActive) => ({
  padding: 10,
  verticalAlign: "top",
  background: isActive ? "#f0f7ff" : "#ffffff",
  border: "2px solid #e2e8f0",
  borderRight: "none",
  borderTopLeftRadius: 12,
  borderBottomLeftRadius: 12,
});

const rightCellStyle = (isActive) => ({
  padding: 10,
  verticalAlign: "top",
  background: isActive ? "#f6fbf7" : "#ffffff",
  border: "2px solid #e2e8f0",
  borderLeft: "none",
  borderTopRightRadius: 12,
  borderBottomRightRadius: 12,
});

const ItemTypes = {
  CIRCUITMEMBER: "circuitMember",
};

const formatSiteLabel = (site) => {
  if (!site) return "";
  return `${site.siteId} - ${site.siteName} - ${site.city}`;
};

const MoveCircuitsModal = ({ show, setShow }) => {
  const {
    allSites,
    isLoading,
    selectedSite,
    setIsLoading,
    adoptiveSite,
    setAdoptiveSite,
    rebuildHierarchy,
    handleAdoptiveSite,
  } = useSiteContext();
  const { isAdmin } = useRoleContext();

  const dropdownRef = useRef(null);
  const inputBoxRef = useRef(null);
  const adoptiveSiteRef = useRef(adoptiveSite);

  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [structureGrouping, setStructureGrouping] = useState(() =>
    (selectedSite?.structures ?? [])
      .filter((s) => (s.circuits ?? []).length > 0)
      .sort((a, b) => a.structureId - b.structureId)
      .map((s) => ({
        sourceStructureId: s.structureId,
        sourceStructureName: s.structureName,
        destinations: [
          {
            bucketId:
              crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
            destinationStructureId: null,
            circuits: s.circuits ?? [],
          },
        ],
      })),
  );

  const closeModal = () => {
    setShow(false);
    setAdoptiveSite(null);
    setInputValue("");
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
    setIsDropdownVisible(e.target.value.length >= 3);
  };

  const filterData = useCallback(
    (data, fields) => {
      if (!inputValue || inputValue.length < 3) return [];
      const searchValue = inputValue.toLowerCase().trim();
      const searchTerms = searchValue.split(/\s+/);

      return data.filter((item) =>
        searchTerms.every((term) =>
          fields.some((field) =>
            String(item[field] || "")
              .toLowerCase()
              .includes(term),
          ),
        ),
      );
    },
    [inputValue],
  );

  const filteredSites = useMemo(() => {
    const searched = filterData(
      allSites.filter((s) => s.siteId < 90000),
      filterKeys.site,
    );
    return searched;
  }, [allSites, filterData]);

  const sortedFilteredSites = useMemo(
    () =>
      [...filteredSites]
        .filter((site) => site.siteId !== selectedSite?.siteId)
        .sort((a, b) => a.siteId - b.siteId),
    [filteredSites, selectedSite?.siteId],
  );

  const handleClickOutside = useCallback(
    (event) => {
      const currentSite = adoptiveSiteRef.current;

      const clickedInsideInput = inputBoxRef.current?.contains(event.target);
      const clickedInsideDropdown = dropdownRef.current?.contains(event.target);

      if (clickedInsideInput || clickedInsideDropdown) return;

      setIsDropdownVisible(false);

      const trimmed = inputValue.trim();
      if (trimmed === "") {
        setInputValue(currentSite ? formatSiteLabel(currentSite) : "");
      }
    },
    [inputValue],
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const adoptiveSiteHandler = (siteId) => {
    handleAdoptiveSite(siteId);
    setIsDropdownVisible(false);
  };

  useEffect(() => {
    adoptiveSiteRef.current = adoptiveSite;
    if (adoptiveSite) {
      setInputValue(
        `${
          adoptiveSite.siteId
        } - ${adoptiveSite.siteName} - ${adoptiveSite.city}`,
      );
    }
  }, [adoptiveSite]);

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 7500);
  };

  const handleSave = async () => {
    if (!adoptiveSite) {
      showError("Please select a destination site first.");
      return;
    }
    setIsLoading(true);
    try {
      const circuits = structureGrouping.flatMap((structure) =>
        structure.destinations
          .filter((destination) => destination.destinationStructureId != null)
          .flatMap((destination) =>
            (destination.circuits ?? []).map((c) => ({
              circuitGmId: c.circuitGmId,
              structureId: destination.destinationStructureId,
            })),
          ),
      );
      const results = await useApi.moveCircuitToStructure(circuits);
      console.log("results: ", results?.returnData);
      rebuildHierarchy({
        circuits: results?.returnData?.updatedCircuits,
        groups: results?.returnData?.updatedGroups,
        assignments: results?.returnData?.updatedGroupAssignments,
      });
    } finally {
      closeModal();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !selectedSite) return;

    setStructureGrouping(
      (selectedSite.structures ?? [])
        .filter((s) => (s.circuits ?? []).length > 0)
        .sort((a, b) => a.structureId - b.structureId)
        .map((s) => ({
          sourceStructureId: s.structureId,
          sourceStructureName: s.structureName,
          destinations: [
            {
              bucketId:
                crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
              destinationStructureId: null,
              circuits: s.circuits ?? [],
            },
          ],
        })),
    );
  }, [show, selectedSite]);

  useEffect(() => {
    if (!show) return;

    setStructureGrouping((prev) =>
      prev.map((structure) => ({
        ...structure,
        destinations: (structure.destinations ?? []).map((d) => ({
          ...d,
          destinationStructureId: null,
        })),
      })),
    );
  }, [show, adoptiveSite?.siteId]);

  const isReadyToSave = useMemo(() => {
    if (!adoptiveSite || !isAdmin) return false;

    return (structureGrouping ?? []).every((structure) =>
      (structure.destinations ?? []).every(
        (d) =>
          d.destinationStructureId != null &&
          d.destinationStructureId !== "" &&
          (d.circuits?.length ?? 0) > 0,
      ),
    );
  }, [adoptiveSite, isAdmin, structureGrouping]);

  return (
    <Container>
      <Modal show={show} onHide={closeModal} dialogClassName="move-modal">
        {isLoading && (
          <div className={styles.overlay}>
            <Spinner animation="border" variant="light" />
          </div>
        )}

        <Modal.Header
          style={{ border: "none", flexShrink: 0 }}
          className="ms-2 me-2"
        >
          <Modal.Title
            style={{
              color: "navy",
              textAlign: "center",
              width: "100%",
              fontWeight: "bold",
            }}
          >
            Move Circuits Between Sites
          </Modal.Title>
        </Modal.Header>

        <Row
          style={{
            padding: "0 1rem",
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <Col
            style={{
              fontWeight: "600",
              fontSize: "1rem",
              textAlign: "center",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {selectedSite?.siteName} ({selectedSite?.siteId})
          </Col>
          <Col
            style={{
              fontWeight: "600",
              fontSize: "1rem",
              textAlign: "center",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <span>Move To</span>
              <Icon
                name="moveToArrow"
                overrides={{
                  style: {
                    marginTop: 4,
                    opacity: 0.55,
                    transform: "scaleX(2.2)",
                  },
                }}
              />
            </div>
          </Col>
          <Col
            style={{
              fontWeight: "600",
              fontSize: "1rem",
              textAlign: "center",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <Row>
              <Form>
                <Form.Group className="position-relative">
                  <div className="d-flex  w-100">
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id="tooltip-view-site-details">
                          Search by Site (Enter at least 3 characters)
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        ref={inputBoxRef}
                        disabled={isLoading}
                        type="text"
                        placeholder="Search Destination Site..."
                        value={inputValue}
                        onChange={handleChange}
                        className="flex-grow-1"
                        style={{ minWidth: 320 }}
                      />
                    </OverlayTrigger>
                  </div>

                  {isDropdownVisible && (
                    <Card
                      ref={dropdownRef}
                      className="position-absolute w-100"
                      style={{ top: "100%", zIndex: 999 }}
                    >
                      <Dropdown
                        className="text-start"
                        style={{ maxHeight: "40vh", overflowY: "auto" }}
                      >
                        {sortedFilteredSites.length > 0 ? (
                          <>
                            {sortedFilteredSites.map((site) => (
                              <Dropdown.Item
                                key={site.siteId}
                                className="p-0 dropdown-item-hover w-100 text-truncate"
                                onClick={() => adoptiveSiteHandler(site.siteId)}
                              >
                                {site.siteId} - {site.siteName}
                              </Dropdown.Item>
                            ))}
                          </>
                        ) : (
                          <div className="p-2 text-muted">
                            No matching sites
                          </div>
                        )}
                      </Dropdown>
                    </Card>
                  )}
                </Form.Group>
              </Form>
            </Row>
            {adoptiveSite && (
              <div
                className="mt-2"
                style={{
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {adoptiveSite?.countryCode ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-country">
                        {adoptiveSite.country}
                      </Tooltip>
                    }
                  >
                    <FlagIcon
                      code={countries.alpha3ToAlpha2(adoptiveSite.countryCode)}
                      style={{ width: "25px", height: "auto" }}
                    />
                  </OverlayTrigger>
                ) : (
                  <span style={{ fontWeight: "bold" }}>
                    {adoptiveSite?.country}
                  </span>
                )}
                {"  "}
                <span
                  style={{
                    fontSize: "0.9rem",
                    wordBreak: "break-word",
                  }}
                >
                  {[
                    adoptiveSite?.addressOne,
                    adoptiveSite?.addressTwo,
                    adoptiveSite?.city,
                    adoptiveSite?.state,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
          </Col>
        </Row>
        <div
          style={{
            padding: "0 1rem",
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Icon name="discard" onClick={closeModal} />
            <Icon
              name="save"
              onClick={isReadyToSave ? handleSave : undefined}
              overrides={{
                style: {
                  opacity: isReadyToSave ? 1 : 0.4,
                  pointerEvents: isReadyToSave ? "auto" : "none",
                  cursor: isReadyToSave ? "pointer" : "not-allowed",
                },
                tooltip: isReadyToSave
                  ? "Save Changes"
                  : "Select a destination structure for every destination row",
              }}
            />{" "}
          </div>
        </div>
        <div
          style={{
            color: "red",
            padding: "0 1rem",
            display: "flex",
            justifyContent: "center",
            minHeight: "1.5rem",
            visibility: error ? "visible" : "hidden",
          }}
        >
          {error || ""}
        </div>

        <Modal.Body
          style={{
            overflowY: "auto",
            flex: 1,
            padding: "1rem",
            paddingTop: 0,
          }}
        >
          <Table bordered hover style={{ tableLayout: "fixed", width: "100%" }}>
            <colgroup>
              <col style={{ width: SOURCE_W }} />
              <col />
              <col style={{ width: DEST_W }} />
            </colgroup>

            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "center",
                    padding: "8px",
                    verticalAlign: "middle",
                  }}
                >
                  Source
                </th>

                <th
                  style={{
                    padding: 0,
                    background: "#00000000",
                    verticalAlign: "middle",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                      paddingLeft: 8,
                      height: "36px",
                    }}
                  >
                    <div style={{ width: 32 }} />
                    <div
                      style={{ flex: 1, padding: "0 8px", textAlign: "center" }}
                    >
                      Circuit
                    </div>
                    <div
                      style={{ flex: 1, padding: "0 8px", textAlign: "center" }}
                    >
                      Carrier
                    </div>
                    <div
                      style={{ flex: 1, padding: "0 8px", textAlign: "center" }}
                    >
                      LEC
                    </div>
                    <div style={{ width: 50 }} />
                  </div>
                </th>

                <th
                  style={{
                    textAlign: "center",
                    padding: "8px",
                    verticalAlign: "middle",
                  }}
                >
                  Destination
                </th>
              </tr>
            </thead>

            <tbody>
              {structureGrouping.map((structure) => (
                <GroupRow
                  key={structure.sourceStructureId}
                  structure={structure}
                  adoptiveSite={adoptiveSite}
                  setStructureGrouping={setStructureGrouping}
                  destWidth={DEST_W}
                />
              ))}
            </tbody>
          </Table>

          <div style={{ marginTop: 8, fontStyle: "italic", fontSize: 12 }}>
            Tip: Choose one or more destination structures and drag circuits to
            assign them accordingly.
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

const GroupRow = ({ structure, setStructureGrouping, adoptiveSite }) => {
  const moveCircuitBetweenBuckets = (
    sourceStructureId,
    fromBucketId,
    toBucketId,
    circuitGmId,
  ) => {
    setStructureGrouping((prev) =>
      prev.map((src) => {
        if (src.sourceStructureId !== sourceStructureId) return src;

        const fromBucket = src.destinations.find(
          (d) => d.bucketId === fromBucketId,
        );
        if (!fromBucket) return src;

        const circuit = (fromBucket.circuits ?? []).find(
          (c) => c.circuitGmId === circuitGmId,
        );
        if (!circuit) return src;

        return {
          ...src,
          destinations: src.destinations.map((d) => {
            if (d.bucketId === fromBucketId) {
              return {
                ...d,
                circuits: d.circuits.filter(
                  (c) => c.circuitGmId !== circuitGmId,
                ),
              };
            }
            if (d.bucketId === toBucketId) {
              if (d.circuits.some((c) => c.circuitGmId === circuitGmId))
                return d;
              return { ...d, circuits: [...d.circuits, circuit] };
            }
            return d;
          }),
        };
      }),
    );
  };

  const BucketDropZone = ({
    sourceStructureId,
    bucket,
    onDropCircuit,
    children,
  }) => {
    const [{ isOver }, drop] = useDrop({
      accept: ItemTypes.CIRCUITMEMBER,
      drop: (item) => {
        if (item.sourceStructureId !== sourceStructureId) return;
        if (item.fromBucketId === bucket.bucketId) return;
        onDropCircuit(item.fromBucketId, bucket.bucketId, item.circuitGmId);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    return (
      <div
        ref={drop}
        style={{
          minWidth: 250,
          verticalAlign: "top",
          padding: 0,
          background: "#f8f9fa",
          border: isOver ? "2px dashed #6c757d" : "2px dashed transparent",
          borderRadius: 8,
          transition: "border 120ms ease",
        }}
      >
        {children}
        {(bucket.circuits?.length ?? 0) === 0 && (
          <div
            style={{
              color: "#6c757d",
              fontStyle: "italic",
              marginTop: 8,
              textAlign: "center",
              padding: "12px 0",
              width: "100%",
            }}
          >
            Drag circuits here to split destination
          </div>
        )}
      </div>
    );
  };

  const addAdditionalDestinationRow = () => {
    if (currentBucketCount >= totalCircuitsInSource) return;
    if (remainingAdoptiveStructures.length === 0) return;
    setStructureGrouping((prev) =>
      prev.map((src) => {
        if (src.sourceStructureId !== structure.sourceStructureId) return src;

        const alreadyHasEmpty = (src.destinations ?? []).some(
          (d) =>
            d.destinationStructureId == null && (d.circuits?.length ?? 0) === 0,
        );
        if (alreadyHasEmpty) return src;

        const newDest = {
          bucketId: crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
          destinationStructureId: null,
          circuits: [],
        };

        return {
          ...src,
          destinations: [...(src.destinations ?? []), newDest],
        };
      }),
    );
  };

  const setDestinationForBucket = (bucketId, destinationStructureId) => {
    setStructureGrouping((prev) =>
      prev.map((src) =>
        src.sourceStructureId !== structure.sourceStructureId
          ? src
          : {
              ...src,
              destinations: (src.destinations ?? []).map((b) =>
                b.bucketId === bucketId
                  ? {
                      ...b,
                      destinationStructureId:
                        destinationStructureId == null
                          ? null
                          : Number(destinationStructureId),
                    }
                  : b,
              ),
            },
      ),
    );
  };

  const selectedDestinationIds = useMemo(
    () =>
      new Set(
        (structure.destinations ?? [])
          .map((d) => d.destinationStructureId)
          .filter((id) => id != null),
      ),
    [structure.destinations],
  );

  const remainingAdoptiveStructures = useMemo(() => {
    if (!adoptiveSite?.structures?.length) return [];
    return adoptiveSite.structures.filter(
      (s) => !selectedDestinationIds.has(s.structureId),
    );
  }, [adoptiveSite?.structures, selectedDestinationIds]);

  const totalCircuitsInSource = (structure.destinations ?? []).reduce(
    (sum, d) => sum + (d.circuits?.length ?? 0),
    0,
  );

  const currentBucketCount = structure.destinations?.length ?? 0;

  const lastDestination =
    structure.destinations?.[structure.destinations.length - 1];

  const lastIsComplete =
    lastDestination?.destinationStructureId != null &&
    (lastDestination.circuits?.length ?? 0) > 0;

  const canAdd =
    lastIsComplete &&
    remainingAdoptiveStructures.length > 0 &&
    currentBucketCount < totalCircuitsInSource;

  const getOptionsForBucket = (bucket) => {
    if (!adoptiveSite?.structures?.length) return [];

    return adoptiveSite.structures
      .slice()
      .sort((a, b) => a.structureId - b.structureId)
      .filter((s) => {
        if (
          bucket.destinationStructureId != null &&
          s.structureId === bucket.destinationStructureId
        ) {
          return true;
        }
        return !selectedDestinationIds.has(s.structureId);
      });
  };

  const removeDestinationBucket = (bucketIdToRemove) => {
    setStructureGrouping((prev) =>
      prev.map((src) => {
        if (src.sourceStructureId !== structure.sourceStructureId) return src;

        if (src.destinations?.[0]?.bucketId === bucketIdToRemove) return src;

        return {
          ...src,
          destinations: (src.destinations ?? []).filter(
            (d) => d.bucketId !== bucketIdToRemove,
          ),
        };
      }),
    );
  };

  return (
    <tr>
      <td>
        <div
          style={{
            padding: "6px 10px",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            background: "#f8f9fa",
            fontWeight: 600,
          }}
        >
          {structure.sourceStructureId} - {structure.sourceStructureName}
        </div>
      </td>

      <td colSpan={2} style={{ padding: 0 }}>
        <Table
          style={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: "0 10px",
          }}
        >
          <colgroup>
            <col />
            <col style={{ width: DEST_W }} />
          </colgroup>

          <tbody>
            {structure.destinations.map((destination) => (
              <tr key={destination.bucketId}>
                <td style={leftCellStyle(true)}>
                  <BucketDropZone
                    sourceStructureId={structure.sourceStructureId}
                    bucket={destination}
                    onDropCircuit={(fromBucketId, toBucketId, circuitGmId) =>
                      moveCircuitBetweenBuckets(
                        structure.sourceStructureId,
                        fromBucketId,
                        toBucketId,
                        circuitGmId,
                      )
                    }
                  >
                    {(destination.circuits ?? []).map((circuit) => (
                      <CircuitCard
                        key={circuit.circuitGmId}
                        circuit={circuit}
                        bucketId={destination.bucketId}
                        sourceStructureId={structure.sourceStructureId}
                      />
                    ))}
                  </BucketDropZone>
                </td>

                <td style={rightCellStyle(true)}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        !adoptiveSite ? (
                          <Tooltip id="destination-tooltip">
                            Please Select Destination Site First
                          </Tooltip>
                        ) : (
                          <></>
                        )
                      }
                    >
                      <span style={{ width: "100%" }}>
                        <Form.Select
                          disabled={!adoptiveSite}
                          value={
                            !adoptiveSite
                              ? "NO_SITE"
                              : (destination.destinationStructureId ?? "")
                          }
                          onChange={(e) =>
                            setDestinationForBucket(
                              destination.bucketId,
                              e.target.value ? Number(e.target.value) : null,
                            )
                          }
                        >
                          {!adoptiveSite && (
                            <option value="NO_SITE">
                              Select Destination Structure
                            </option>
                          )}

                          {adoptiveSite && (
                            <option value="" disabled hidden>
                              Select Destination Structure
                            </option>
                          )}

                          {getOptionsForBucket(destination).map((s) => (
                            <option key={s.structureId} value={s.structureId}>
                              {s.structureId} - {s.structureName}
                            </option>
                          ))}
                        </Form.Select>
                      </span>
                    </OverlayTrigger>

                    {(destination.circuits?.length ?? 0) === 0 && (
                      <Icon
                        name="remove"
                        onClick={() =>
                          removeDestinationBucket(destination.bucketId)
                        }
                        overrides={{
                          tooltip: "Remove this destination bucket",
                          tooltipPlacement: "top",
                          size: ".8rem",
                          style: { cursor: "pointer" },
                        }}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {canAdd && (
              <tr>
                <td />
                <td>
                  <p className="ps-2 m-0">
                    <Icon name="add" onClick={addAdditionalDestinationRow} />
                    <i> Add Destination Strucuture</i>
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </td>
    </tr>
  );
};

const CircuitCard = ({ circuit, bucketId, sourceStructureId }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CIRCUITMEMBER,
    item: {
      circuitGmId: circuit.circuitGmId,
      sourceStructureId,
      fromBucketId: bucketId,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <div
      ref={drag}
      style={{
        display: "flex",
        alignItems: "center",
        opacity: isDragging ? 0.5 : 1,
        padding: "0 0 0 8px",
        margin: 0,
        minHeight: 36,
        borderBottom: "1px solid #e0e0e0",
        backgroundColor: "#e1ecf7",
        borderRadius: 0,
        cursor: "move",
        userSelect: "none",
      }}
    >
      <div style={{ width: 32, display: "flex", alignItems: "center" }}>
        <Icon
          name="moveBars"
          overrides={{
            tooltipPlacement: "top",
            size: "1rem",
            style: { cursor: "move" },
          }}
        />
      </div>

      <div style={{ flex: 1, display: "flex", width: "100%" }}>
        <div
          style={{
            flex: 1,
            padding: "0 8px",
            minWidth: 0,
            fontWeight: 500,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {circuit.circuitVendorId}
        </div>

        <div
          style={{
            flex: 1,
            padding: "0 8px",
            minWidth: 0,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {circuit.carrier}
        </div>

        <div
          style={{
            flex: 1,
            padding: "0 8px",
            minWidth: 0,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {circuit.lec}
        </div>
      </div>
    </div>
  );
};

export default MoveCircuitsModal;
